import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { Checkbox } from '../../../components/ui/Checkbox';

const EquipmentTable = ({ equipment, selectedEquipment, onSelectEquipment, onSelectAll, onSort, sortConfig, onViewEquipment }) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      operational: { bg: 'bg-success/10', text: 'text-success', label: 'Operational', icon: 'CheckCircle2' },
      maintenance: { bg: 'bg-warning/10', text: 'text-warning', label: 'Maintenance', icon: 'Wrench' },
      'out-of-service': { bg: 'bg-error/10', text: 'text-error', label: 'Out of Service', icon: 'AlertCircle' },
      retired: { bg: 'bg-muted', text: 'text-muted-foreground', label: 'Retired', icon: 'Archive' }
    };

    const config = statusConfig?.[status] || statusConfig?.operational;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        <Icon name={config?.icon} size={12} />
        {config?.label}
      </span>
    );
  };

  const getConditionBadge = (condition) => {
    const conditionConfig = {
      excellent: { bg: 'bg-success/10', text: 'text-success', label: 'Excellent' },
      good: { bg: 'bg-primary/10', text: 'text-primary', label: 'Good' },
      fair: { bg: 'bg-warning/10', text: 'text-warning', label: 'Fair' },
      poor: { bg: 'bg-error/10', text: 'text-error', label: 'Poor' }
    };

    const config = conditionConfig?.[condition] || conditionConfig?.good;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        {config?.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getSortIcon = (column) => {
    if (sortConfig?.column !== column) return 'ChevronsUpDown';
    return sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown';
  };

  const allSelected = equipment?.length > 0 && selectedEquipment?.length === equipment?.length;
  const someSelected = selectedEquipment?.length > 0 && selectedEquipment?.length < equipment?.length;

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="overflow-x-auto custom-scrollbar">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left w-12">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('name')}
                  className="flex items-center gap-2 text-xs font-semibold text-foreground uppercase tracking-wider hover:text-primary transition-colors"
                >
                  Equipment
                  <Icon name={getSortIcon('name')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('category')}
                  className="flex items-center gap-2 text-xs font-semibold text-foreground uppercase tracking-wider hover:text-primary transition-colors"
                >
                  Category
                  <Icon name={getSortIcon('category')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-semibold text-foreground uppercase tracking-wider">
                  Location
                </span>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('status')}
                  className="flex items-center gap-2 text-xs font-semibold text-foreground uppercase tracking-wider hover:text-primary transition-colors"
                >
                  Status
                  <Icon name={getSortIcon('status')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('condition')}
                  className="flex items-center gap-2 text-xs font-semibold text-foreground uppercase tracking-wider hover:text-primary transition-colors"
                >
                  Condition
                  <Icon name={getSortIcon('condition')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('lastMaintenance')}
                  className="flex items-center gap-2 text-xs font-semibold text-foreground uppercase tracking-wider hover:text-primary transition-colors"
                >
                  Last Maintenance
                  <Icon name={getSortIcon('lastMaintenance')} size={14} />
                </button>
              </th>
              <th className="px-4 py-3 text-center w-24">
                <span className="text-xs font-semibold text-foreground uppercase tracking-wider">
                  Actions
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {equipment?.map((item) => (
              <tr
                key={item?.id}
                className="hover:bg-muted/30 transition-colors cursor-pointer"
                onClick={() => onViewEquipment(item?.id)}
              >
                <td className="px-4 py-3" onClick={(e) => e?.stopPropagation()}>
                  <Checkbox
                    checked={selectedEquipment?.includes(item?.id)}
                    onChange={(e) => onSelectEquipment(item?.id, e?.target?.checked)}
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                      <Image
                        src={item?.image}
                        alt={item?.imageAlt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{item?.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{item?.serialNumber}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Icon name={item?.category === 'Cardio' ? 'Activity' : 'Dumbbell'} size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{item?.category}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Icon name="MapPin" size={14} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{item?.location}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {getStatusBadge(item?.status)}
                </td>
                <td className="px-4 py-3">
                  {getConditionBadge(item?.condition)}
                </td>
                <td className="px-4 py-3">
                  <div>
                    <p className="text-sm text-foreground">{formatDate(item?.lastMaintenance)}</p>
                    <p className="text-xs text-muted-foreground">Next: {formatDate(item?.nextMaintenance)}</p>
                  </div>
                </td>
                <td className="px-4 py-3" onClick={(e) => e?.stopPropagation()}>
                  <div className="flex items-center justify-center gap-1">
                    <button
                      className="p-2 hover:bg-muted rounded-lg transition-colors"
                      title="View Details"
                      onClick={() => onViewEquipment(item?.id)}
                    >
                      <Icon name="Eye" size={16} className="text-muted-foreground" />
                    </button>
                    <button
                      className="p-2 hover:bg-muted rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Icon name="Edit" size={16} className="text-muted-foreground" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EquipmentTable;